import React, { useEffect, useState } from "react";
import {
  Upload,
  Notification,
  toast,
} from "components/ui";
import {
  HiArrowSmRight,
  HiOutlineCloudUpload,
  HiOutlineDocumentText,
  HiOutlineTrash,
} from "react-icons/hi";

export const ACCEPT_CERT_IMAGE_AND_PDF =
  "image/png, image/gif, image/jpeg, application/pdf";

const CertAttachmentCard = ({
  label,
  href,
  hideDelete,
  onDelete,
  deleteLabel,
}) => (
  <div className="group flex w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ring-1 ring-black/[0.04] transition-all hover:border-kostebek-500/70 hover:shadow-md dark:border-gray-600 dark:bg-gray-800 dark:ring-white/[0.06] dark:hover:border-kostebek-600/80">
    {href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-kostebek-50/90 dark:hover:bg-gray-700/40"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-kostebek-100/90 text-gray-800 dark:bg-gray-700 dark:text-kostebek-300">
          <HiOutlineDocumentText className="h-6 w-6" />
        </span>
        <span className="min-w-0 flex-1 font-semibold text-gray-900 dark:text-gray-100">
          {label}
        </span>
        <HiArrowSmRight className="mr-0.5 h-5 w-5 shrink-0 text-gray-400 transition-transform duration-150 group-hover:translate-x-0.5 dark:text-gray-500" />
      </a>
    ) : (
      <div className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3.5 opacity-75">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
          <HiOutlineDocumentText className="h-6 w-6" />
        </span>
        <span className="min-w-0 flex-1 font-semibold text-gray-900 dark:text-gray-100">
          {label}
        </span>
      </div>
    )}
    {!hideDelete && (
      <button
        type="button"
        className="flex shrink-0 items-center justify-center border-l border-gray-200 px-4 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 dark:border-gray-600 dark:hover:bg-red-950/30 dark:hover:text-red-400"
        onClick={onDelete}
        aria-label={deleteLabel}
      >
        <HiOutlineTrash className="h-5 w-5" />
      </button>
    )}
  </div>
);

/**
 * Tekil belge yükleme (PDF / görsel), kart tasarımı ve isteğe bağlı toast.
 *
 * `readOnly`: sadece mevcut / seçili dosya kartları — yükleme ve silme yok.
 */
const CertPdfAttachmentField = ({
  field,
  form,
  existList = [],
  fileList = [],
  onSetFormFile,
  handleCertDelete,
  t,
  readOnly = false,
  showReminderToast,
  disabledUpload = false,
}) => {
  const newFilesKey = fileList
    .map((f) => `${f.name}-${f.size}-${f.lastModified}`)
    .join("|");
  const [objectUrls, setObjectUrls] = useState([]);
  const viewFileLabel = t("wastes.form.certViewFile");
  const shouldRemindSave =
    typeof showReminderToast === "boolean"
      ? showReminderToast
      : !readOnly && !disabledUpload;

  useEffect(() => {
    const urls = fileList.map((file) => URL.createObjectURL(file));
    setObjectUrls(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `newFilesKey` fingerprints file contents
  }, [newFilesKey]);

  const hasAny = existList.length > 0 || fileList.length > 0;
  const hideDelete = readOnly;

  if (readOnly && !hasAny) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      {hasAny ? (
        <div className="flex flex-col gap-3">
          {existList.map((item) => (
            <CertAttachmentCard
              key={item.id ?? item.url}
              label={viewFileLabel}
              href={item.url}
              hideDelete={hideDelete}
              deleteLabel={t("misc.delete")}
              onDelete={
                hideDelete || !handleCertDelete
                  ? undefined
                  : () => handleCertDelete(form, field, item, existList)
              }
            />
          ))}
          {fileList.map((file, idx) => (
            <CertAttachmentCard
              key={`${file.name}-${idx}`}
              label={viewFileLabel}
              href={objectUrls[idx] || null}
              hideDelete={hideDelete}
              deleteLabel={t("misc.delete")}
              onDelete={
                hideDelete || !handleCertDelete
                  ? undefined
                  : () => handleCertDelete(form, field, file, fileList)
              }
            />
          ))}
        </div>
      ) : (
        <>
          {!readOnly && !disabledUpload ? (
            <Upload
              className="group w-full max-w-md !min-h-fit !rounded-xl !border-0 bg-transparent !p-0 !shadow-none hover:!border-transparent hover:!shadow-none"
              showList={false}
              draggable
              multiple={false}
              uploadLimit={1}
              onChange={(files) => {
                onSetFormFile?.(form, field, files);
                if (shouldRemindSave && files?.length > 0) {
                  toast.push(
                    <Notification
                      title={t("wastes.form.certUploadSaveReminderTitle")}
                      type="info"
                      duration={4500}
                    >
                      {t("wastes.form.certUploadSaveReminder")}
                    </Notification>,
                    { placement: "top-center" },
                  );
                }
              }}
              fileList={fileList}
              accept={ACCEPT_CERT_IMAGE_AND_PDF}
            >
              <div className="pointer-events-none flex min-h-[120px] w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 bg-kostebek-50/70 px-5 py-6 text-center shadow-sm transition-[border-color,background-color] group-hover:border-kostebek-500 group-hover:bg-kostebek-50 dark:border-gray-600 dark:bg-gray-800/40 dark:group-hover:border-kostebek-600 dark:group-hover:bg-gray-800/70">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-gray-600 shadow-inner dark:bg-gray-700 dark:text-kostebek-400">
                  <HiOutlineCloudUpload className="h-7 w-7" />
                </span>
                <p className="text-base font-semibold text-gray-800 dark:text-gray-100">
                  {t("misc.upload")}
                </p>
                <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                  {t("wastes.form.certUploadFormatsHint")}
                </p>
              </div>
            </Upload>
          ) : null}
        </>
      )}
    </div>
  );
};

export default CertPdfAttachmentField;
